import { Auth } from 'aws-amplify';
import appConfig from '../config/app-config';

const API = appConfig.apis.collateralApiEnv;
const CONVERT_XML_API = appConfig.apis.convertXmlApiEnv;
const baseUrl = API.address;

export class ApiError extends Error {
    constructor(public status: number, message: string) {
        super(message);
    }
}

const getHeaders = async (): Promise<Record<string, string>> => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    return {
        'Content-Type': 'application/json',
        'x-api-key': API.key,
        'Authorization': `Bearer ${token}`,
    };
};

const handleResponse = async (res: Response) => {
    if (res.ok) return res.json();
    throw new ApiError(res.status, `API error: ${res.status}`);
};

const get = async (path: string) => handleResponse(await fetch(`${baseUrl}${path}`, { headers: await getHeaders() }));

const put = async (path: string, data: Record<string, unknown>) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { method: 'PUT', headers: await getHeaders(), body: JSON.stringify(data) }));

const post = async (path: string, data: Record<string, unknown>) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { method: 'POST', headers: await getHeaders(), body: JSON.stringify(data) }));

const del = async (path: string, data?: Record<string, unknown>) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { method: 'DELETE', headers: await getHeaders(), body: data ? JSON.stringify(data) : undefined }));

const qs = (params: Record<string, unknown>) => {
    const s = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v != null && v !== '') s.set(k, String(v)); });
    const str = s.toString();
    return str ? `?${str}` : '';
};

export const collateralApi = {
    getGuarantees: (params?: {
        page?: number; pageSize?: number;
        operationStatus?: string; guaranteeStatus?: string;
        traderStatus?: string; validationStatus?: string;
        intensity?: string; operationType?: string;
        search?: string; sortField?: string; sortOrder?: string;
    }) => get(`/guarantees/page-list${qs(params || {})}`),

    getGuaranteeView: (id: string) => get(`/guarantees/${id}/page-view`),

    updateGuarantee: (id: string, data: Record<string, unknown>) => put(`/guarantees/${id}`, data),

    getValidations: (params?: { page?: number; pageSize?: number; status?: string; search?: string }) =>
        get(`/validation/page-list${qs(params || {})}`),

    getValidationView: (id: string, params?: { page?: number; pageSize?: number; status?: string; search?: string }) =>
        get(`/validation/${id}/page-view${qs(params || {})}`),

    updateValidation: (id: string, data: Record<string, unknown>) => put(`/validation/${id}`, data),

    createAnalysis: (data: Record<string, unknown>) => post(`/collateral-analysis`, data),

    populateReceivables: (analysisId: string, documents: Record<string, unknown>[]) =>
        post(`/collateral-analysis/${analysisId}/receivables`, { documents }),

    getOperationData: async (operationCode: string, operationId?: string) => {
        const headers = await getHeaders();
        headers['x-api-key'] = CONVERT_XML_API.key;
        let id = operationId;
        if (!id) {
            const listRes = await fetch(`${CONVERT_XML_API.address}/operations?search=${encodeURIComponent(operationCode)}&pageSize=10`, { headers });
            const listData = await listRes.json();
            const op = listData?.list?.find((o: Record<string, unknown>) => o.operationCode === operationCode);
            if (!op) throw new ApiError(404, 'Operation not found');
            id = op.id;
        }
        const res = await fetch(`${CONVERT_XML_API.address}/operations/${id}`, { headers });
        return handleResponse(res);
    },

    registerDraweeAction: (analysisId: string, taxId: string, action: string, observation?: string) =>
        post(`/validation/${analysisId}/drawees/${encodeURIComponent(taxId)}/actions`, { action, observation }),

    linkContact: (analysisId: string, taxId: string, contactId: string, contactStatus?: string) =>
        put(`/validation/${analysisId}/drawees/${encodeURIComponent(taxId)}/link-contact`, { contactId, contactStatus }),

    validateDrawee: (analysisId: string, taxId: string, channel: string) =>
        post(`/validation/${analysisId}/drawees/${encodeURIComponent(taxId)}/validate`, { channel }),

    validateReceivables: (analysisId: string, taxId: string, receivableIds: string[], channel: string) =>
        post(`/validation/${analysisId}/drawees/${encodeURIComponent(taxId)}/validate-receivables`, { receivableIds, channel }),

    markNotConfirmed: (analysisId: string, receivableId: string, observation: string) =>
        put(`/validation/${analysisId}/receivables/${receivableId}/not-confirmed`, { observation }),

    unmarkNotConfirmed: (analysisId: string, receivableId: string) =>
        del(`/validation/${analysisId}/receivables/${receivableId}/not-confirmed`),

    undoDraweeValidation: (analysisId: string, taxId: string) =>
        post(`/validation/${analysisId}/drawees/${encodeURIComponent(taxId)}/undo-validation`, {}),

    updatePriority: (analysisId: string, priority: boolean) =>
        put(`/validation/${analysisId}/priority`, { priority }),
};
